import type { Aircraft, AircraftType, Airport, IlsCategory } from "../types";

const ILS_ORDER: IlsCategory[] = ["cat1", "cat2", "cat3"];

export interface AircraftDispatchCapabilities {
  etopsMinutes: number;
  maxTakeoffWeightKg: number;
  ilsCategory: IlsCategory;
}

export function defaultAircraftCapabilities(type: AircraftType | undefined): AircraftDispatchCapabilities {
  return {
    etopsMinutes: defaultEtopsMinutes(type),
    maxTakeoffWeightKg: type?.maxTakeoffWeightKg ?? 75_000,
    ilsCategory: defaultIlsCategory(type)
  };
}

export function normalizeIlsCategory(value: unknown, fallback: IlsCategory = "cat1"): IlsCategory {
  if (value === "cat1" || value === "cat2" || value === "cat3") {
    return value;
  }
  return fallback;
}

export function formatIlsCategory(value: IlsCategory): string {
  return value.toUpperCase();
}

export function effectiveAircraftCapabilities(aircraft: Aircraft, type: AircraftType): AircraftDispatchCapabilities {
  const defaults = defaultAircraftCapabilities(type);
  return {
    etopsMinutes: finiteNumber(aircraft.etopsMinutes, defaults.etopsMinutes),
    maxTakeoffWeightKg: finiteNumber(aircraft.maxTakeoffWeightKg, defaults.maxTakeoffWeightKg),
    ilsCategory: normalizeIlsCategory(aircraft.ilsCategory, defaults.ilsCategory)
  };
}

export function validateAircraftCapabilities(input: {
  aircraft: Aircraft;
  type: AircraftType;
  origin: Airport;
  destination: Airport;
  distanceKm: number;
}): string[] {
  const notes: string[] = [];
  const requiredRangeKm = Math.ceil(input.distanceKm * 1.08);
  if (input.type.rangeKm < requiredRangeKm) {
    notes.push(`航程不足：${input.type.name} 可飞 ${input.type.rangeKm}km，任务需要 ${requiredRangeKm}km。`);
  }

  const capabilities = effectiveAircraftCapabilities(input.aircraft, input.type);
  const etopsRequirement = requiredEtopsMinutesForRoute(input.origin, input.destination, input.distanceKm);
  if (capabilities.etopsMinutes < etopsRequirement) {
    notes.push(`${input.aircraft.registration} ETOPS 不满足：需要 ETOPS ${etopsRequirement}，当前 ${capabilities.etopsMinutes}。`);
  }

  const requiredTakeoffWeightKg = estimateTakeoffWeightKg(input.type, input.distanceKm);
  if (capabilities.maxTakeoffWeightKg < requiredTakeoffWeightKg) {
    notes.push(aircraftTakeoffWeightMismatchNote(input.aircraft, capabilities, requiredTakeoffWeightKg));
  }

  const requiredIlsCategory = input.destination.requiredIlsCategory ?? "cat1";
  if (compareIlsCategory(capabilities.ilsCategory, requiredIlsCategory) < 0) {
    notes.push(`${input.aircraft.registration} 盲降类别不一致：${formatIlsCategory(input.destination.requiredIlsCategory)} 进近需要，当前 ${formatIlsCategory(capabilities.ilsCategory)}。`);
  }
  return notes;
}

export function estimateTakeoffWeightKg(type: AircraftType, distanceKm: number): number {
  const rangeRatio = aircraftRangeRatio(distanceKm, type);
  const payloadAndOperatingWeight = aircraftPayloadAndOperatingWeightKg(type);
  const missionFuel = type.fuelBurnKgPerKm * distanceKm * 1.25;
  const reserve = type.maxFuelKg * (0.14 + rangeRatio * 0.16);
  const estimatedTakeoffWeightKg = payloadAndOperatingWeight + missionFuel + reserve;
  const cappedTakeoffWeightKg = Math.min(type.maxTakeoffWeightKg, estimatedTakeoffWeightKg);
  return Math.round(cappedTakeoffWeightKg);
}

function aircraftTakeoffWeightMismatchNote(aircraft: Aircraft, capabilities: AircraftDispatchCapabilities, requiredTakeoffWeightKg: number): string {
  const dispatchMtowTons = Math.round(capabilities.maxTakeoffWeightKg / 1000);
  const requiredTakeoffWeightTons = Math.round(requiredTakeoffWeightKg / 1000);
  return `${aircraft.registration} 起飞重量不一致：放行 MTOW ${dispatchMtowTons}t，预计需要 ${requiredTakeoffWeightTons}t。`;
}

function aircraftRangeRatio(distanceKm: number, type: AircraftType): number {
  const protectedRangeKm = Math.max(1, type.rangeKm);
  const rawRangeRatio = distanceKm / protectedRangeKm;
  const cappedRangeRatio = Math.min(1, rawRangeRatio);
  return Math.max(0, cappedRangeRatio);
}

function aircraftPayloadAndOperatingWeightKg(type: AircraftType): number {
  const structuralPayloadCapacityKg = type.maxZeroFuelWeightKg - type.operatingEmptyWeightKg;
  const estimatedPayloadKg = type.seatCapacity * 106 + type.bellyCargoCapacityKg * 0.65;
  const cappedPayloadKg = Math.min(structuralPayloadCapacityKg, estimatedPayloadKg);
  return type.operatingEmptyWeightKg + cappedPayloadKg;
}

export function requiredEtopsMinutesForRoute(origin: Airport, destination: Airport, distanceKm: number): number {
  const regions = new Set([origin.regionId, destination.regionId]);
  if (hasPair(regions, "eu", "na") || hasPair(regions, "ea", "na") || hasPair(regions, "oc", "ea") || hasPair(regions, "sa", "af") || hasPair(regions, "sa", "eu")) {
    return 180;
  }
  if (hasPair(regions, "sea", "me") || hasPair(regions, "me", "eu") || hasPair(regions, "af", "eu") || distanceKm >= 6_500) {
    return 120;
  }
  return 0;
}

export function compareIlsCategory(left: IlsCategory, right: IlsCategory): number {
  return ILS_ORDER.indexOf(left) - ILS_ORDER.indexOf(right);
}

function finiteNumber(value: unknown, fallback: number): number {
  let numeric = NaN;
  if (typeof value === "number") {
    numeric = value;
  } else if (typeof value === "string" && value.trim()) {
    numeric = Number(value);
  }
  if (Number.isFinite(numeric)) {
    return numeric;
  }
  return fallback;
}

function hasPair(regions: Set<string>, left: string, right: string): boolean {
  return regions.has(left) && regions.has(right);
}

function defaultEtopsMinutes(type: AircraftType | undefined): number {
  if (!type) {
    return 90;
  }
  if (type.rangeKm >= 12_000) {
    return 330;
  }
  if (type.rangeKm >= 5_500) {
    return 180;
  }
  return 90;
}

function defaultIlsCategory(type: AircraftType | undefined): IlsCategory {
  if (type && type.rangeKm >= 5_500) {
    return "cat3";
  }
  return "cat2";
}
